/** Copyright © 2013-2021 DataYes, All Rights Reserved. */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Spin } from 'antd';
import { stringify } from '../../../utils/qs';
import { encrypt } from '../../../utils/encrypt';
import API from '../../../services';

const StyledWrapper = styled.div`
  text-align: center;

  h2 {
    font-weight: bold;
    color: #333;
    margin: 0;
  }
  .image-wrapper {
    position: relative;
    width: 200px;
    height: 200px;
    margin: 15px auto;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    .cover {
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      p {
        font-weight: bold;
        font-size: 16px;
        color: #fff;
        text-align: center;
        margin-bottom: 10px;
      }
      span {
        width: 110px;
        height: 36px;
        background: #1283d5;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #fff;
        cursor: pointer;
      }
    }
  }
  img {
    width: 210px;
    height: 210px;
  }
  p {
    font-size: 16px;
    color: #999;
    margin: 0;
  }
`;

type Props = {
  afterLogin?: (...args: any[]) => any;
  onRegister: (...args: any[]) => any;
};

export default function LoginByFollowWechat({ afterLogin, onRegister }: Props) {
  const [qrCode, setQrCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const getWechatPublicQR = useCallback(() => {
    API.getWechatPublicQR()
      .then(({ data }: any) => {
        if (data) {
          const { qrCodeUrl, ticket: t } = data;
          setQrCode(qrCodeUrl);
          setTicket(t);
          setErrMsg('');
        }
      })
      .catch(() => {
        setErrMsg('二维码生成有误');
      });
  }, []);

  const getLoginStatus = useCallback(
    (intervalId) => {
      API.getWechatPublicLogin(ticket)
        .then(({ data }: any) => {
          const { status, unionId } = data || {};
          /**
           * status 为 -1：二维码已过期，重新生成并扫码
           * status 为 0: 已生成二维码，等待用户扫描
           * status 为 1: 用户已扫码关注且有绑定微信的账号，直接登录
           * status 为 2: 用户已扫码关注尚未注册，跳转注册功能
           */
          if (status !== 0) {
            if (status === 1) {
              const encryptToken = encrypt(ticket);
              const params = stringify({
                username: encryptToken,
                password: encryptToken,
                identityType: 'WECHAT_RRP_SERVICE_ACCOUNT',
                unionId,
              });
              API.login(params).then((resp: any) => {
                const result = resp?.content.result;

                if (result === 'SUCCESS') {
                  if (typeof afterLogin === 'function') {
                    afterLogin();
                  }
                } else {
                  setErrMsg('登录失败');
                }
              });
            } else if (status === 2) {
              onRegister('register', {
                isBindMobile: true,
                unionId,
              });
            } else if (status === -1) {
              setErrMsg('二维码已失效');
            }

            window.clearInterval(intervalId);
          }
        })
        .catch(() => {});
    },
    [afterLogin, onRegister, ticket]
  );

  useEffect(() => {
    getWechatPublicQR();
  }, [getWechatPublicQR]);

  useEffect(() => {
    let intervalId: any;
    if (ticket) {
      intervalId = setInterval(() => {
        getLoginStatus(intervalId);
      }, 3000);
    }

    return () => {
      window.clearInterval(intervalId);
    };
  }, [ticket, getLoginStatus]);

  return (
    <StyledWrapper>
      <h2>微信扫码登录</h2>
      <div className="image-wrapper">
        <Spin spinning={!qrCode}>
          <img src={qrCode} alt="" />
        </Spin>
        {errMsg && (
          <div className="cover">
            <p>{errMsg}</p>
            <span onClick={getWechatPublicQR}>请点击刷新</span>
          </div>
        )}
      </div>
      <p>扫码关注后，自动登录</p>
    </StyledWrapper>
  );
}
